import { useConfirm } from "@/components/DialogProvider";
import { ScheduleForm } from "@/components/schedule/ScheduleForm";
import { ScheduleCalendar } from "@/components/schedule/ScheduleCalendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useProjectContext } from "@/contexts/ProjectContext";
import * as projectService from "@/features/projects/api/projectService";
import { createFileRoute } from "@tanstack/react-router";
import { Calendar, Settings, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/dashboard/admin/projects/$projectId/schedule/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { project, loading } = useProjectContext();
  const { userProfile } = useAuth();
  const confirm = useConfirm();
  const [pendingScheduleData, setPendingScheduleData] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleScheduleChange = useCallback((scheduleData: any) => {
    setPendingScheduleData(scheduleData);
  }, []);

  const handleScheduleSave = useCallback(async () => {
    if (!project || !userProfile?.uid) return;

    setIsSaving(true);
    try {
      await projectService.updateProject(
        projectId,
        {
          schedule: pendingScheduleData,
          hasSchedule: pendingScheduleData?.enabled || false,
        },
        userProfile.uid
      );

      setPendingScheduleData(null);
      toast.success("Project schedule updated");
    } catch (error) {
      console.error("Error updating project schedule:", error);
      toast.error("Failed to update project schedule");
    } finally {
      setIsSaving(false);
    }
  }, [project, projectId, userProfile?.uid, pendingScheduleData]);

  const handleScheduleDelete = useCallback(async () => {
    if (!project || !userProfile?.uid) return;

    const confirmed = await confirm({
      title: "Delete Schedule?",
      description: "Are you sure you want to delete this project schedule? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    setIsSaving(true);
    try {
      await projectService.updateProject(
        projectId,
        {
          schedule: null,
          hasSchedule: false,
        },
        userProfile.uid
      );

      setPendingScheduleData(null);
      toast.success("Project schedule deleted");
    } catch (error) {
      console.error("Error deleting project schedule:", error);
      toast.error("Failed to delete project schedule");
    } finally {
      setIsSaving(false);
    }
  }, [project, projectId, userProfile?.uid, confirm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-center text-gray-500">Project not found</p>
        </CardContent>
      </Card>
    );
  }

  const isOngoingProject = project.type === "ongoing";

  return (
    <div className="space-y-6">
      {/* Schedule Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              <CardTitle>Schedule Configuration</CardTitle>
            </div>
            <div className="flex gap-2">
              {project.hasSchedule && project.schedule && (
                <Button
                  onClick={handleScheduleDelete}
                  disabled={isSaving}
                  size="sm"
                  variant="destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {isSaving ? "Deleting..." : "Delete Schedule"}
                </Button>
              )}
              {pendingScheduleData !== null && (
                <Button
                  onClick={handleScheduleSave}
                  disabled={isSaving}
                  size="sm"
                >
                  {isSaving ? "Saving..." : "Save Schedule"}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isOngoingProject ? (
            <ScheduleForm
              schedule={project.schedule}
              projectStartDate={project.startDate}
              projectEndDate={project.endDate}
              onChange={handleScheduleChange}
            />
          ) : (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <p className="text-sm text-yellow-800">
                Schedule configuration is only available for ongoing projects.
                Change the project type to "Ongoing" in the Details tab to
                enable scheduling.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Calendar */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            <CardTitle>Schedule Calendar</CardTitle>
          </div>
          <p className="text-sm text-gray-500">
            Click on any day to manually add, update, or remove it from the
            schedule. Changes will be tracked and shown in the timeline.
          </p>
        </CardHeader>
        <CardContent>
          <ScheduleCalendar
            projectId={projectId}
            project={project}
            schedule={project.schedule || null}
          />
        </CardContent>
      </Card>
    </div>
  );
}
